import { ColDef, GridApi, ISetFilterParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import {
  ANIMAL_TYPE_CODES,
  AnimalRow,
  ProcedureRow,
} from '@/services/animal-management';

const ANIMAL_TYPE_CODE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(ANIMAL_TYPE_CODES).map(([label, code]) => [
    String(code),
    label,
  ]),
);

const selectionColumn: ColDef = {
  checkboxSelection: true,
  width: 37,
  sortable: false,
  filter: false,
  resizable: false,
  pinned: 'left',
  suppressMovable: true,
  lockPosition: true,
  suppressHeaderMenuButton: true,
};

export const useAnimalColumns = (): ColDef<AnimalRow>[] => {
  const { t } = useTranslation();

  return [
    selectionColumn,
    {
      field: 'stateCode',
      headerName: t('common.status', 'Status'),
      initialWidth: 100,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['active', 'inactive'],
        defaultValues: ['active'],
        valueFormatter(params: { value: string }) {
          if (params.value === 'active') {
            return t('common.active', 'Active');
          }
          if (params.value === 'inactive') {
            return t('common.inactive', 'Inactive');
          }
        },
        defaultToNothingSelected: true,
      } as ISetFilterParams,
      valueGetter: (params) =>
        params.data?.stateCode === 0 ? 'active' : 'inactive',
      valueFormatter(params) {
        if (params.value === 'active') {
          return t('common.active', 'Active');
        }
        if (params.value === 'inactive') {
          return t('common.inactive', 'Inactive');
        }
        return '';
      },
    },
    {
      field: 'nameTag',
      headerName: t('animalManagement.columns.nameTag', 'Name (Tag)'),
      initialWidth: 105,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'name',
      headerName: t('animalManagement.columns.name', 'Name'),
      initialWidth: 95,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'tag',
      headerName: t('animalManagement.columns.tag', 'Tag'),
      initialWidth: 75,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'breed',
      headerName: t('animalManagement.columns.breed', 'Breed'),
      initialWidth: 105,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'rfidTag',
      headerName: t('animalManagement.columns.rfidTag', 'RFID Tag'),
      initialWidth: 90,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'birthDate',
      headerName: t('animalManagement.columns.birthDate', 'Birth Date'),
      initialWidth: 105,
      filter: 'agDateColumnFilter',
    },
    {
      field: 'ranch',
      headerName: t('animalManagement.columns.ranch', 'Ranch'),
      initialWidth: 160,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'yard',
      headerName: t('animalManagement.columns.yard', 'Yard'),
      initialWidth: 75,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'receivedDate',
      headerName: t('animalManagement.columns.receivedDate', 'Received Date'),
      initialWidth: 120,
      filter: 'agDateColumnFilter',
    },
    {
      field: 'pregnant',
      headerName: t('animalManagement.columns.pregnant', 'Pregnant'),
      initialWidth: 90,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['1', '0'],
        valueFormatter: (params: { value: string }) =>
          params.value === '1' ? t('common.yes', 'Yes') : t('common.no', 'No'),
      } as ISetFilterParams,
    },
    {
      field: 'cowHeifer',
      headerName: t('animalManagement.columns.cowHeifer', 'Cow/Heifer'),
      initialWidth: 100,
      // Donor tog_heifercow; recip Cow/Heifer is Not Required (#35316).
      filter: 'agTextColumnFilter',
    },
    {
      field: 'weight',
      headerName: t('animalManagement.columns.weight', 'Weight'),
      initialWidth: 80,
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'ivfEtStatus',
      headerName: t('animalManagement.columns.ivfEtStatus', 'IVF/ET Status'),
      initialWidth: 115,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'programCategory',
      headerName: t(
        'animalManagement.columns.programCategory',
        'Program Category',
      ),
      initialWidth: 140,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'housing',
      headerName: t('animalManagement.columns.housing', 'Housing'),
      initialWidth: 90,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'shipDate',
      headerName: t('animalManagement.columns.shipDate', 'Ship Date'),
      initialWidth: 100,
      filter: 'agDateColumnFilter',
    },
    {
      field: 'opuProgram',
      headerName: t('animalManagement.columns.opuProgram', 'OPU Program'),
      initialWidth: 110,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'origin',
      headerName: t('animalManagement.columns.origin', 'Origin'),
      initialWidth: 90,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'color',
      headerName: t('animalManagement.columns.color', 'Color'),
      initialWidth: 100,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'recipientStatus',
      headerName: t('animalManagement.columns.recipientStatus', 'Recip Status'),
      initialWidth: 120,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'ocvStatus',
      headerName: t('animalManagement.columns.ocvStatus', 'OCV Status'),
      initialWidth: 110,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'lastDrugUsed',
      headerName: t('animalManagement.columns.lastDrugUsed', 'Last Drug Used'),
      initialWidth: 130,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'hairSample',
      headerName: t('animalManagement.columns.hairSample', 'Hair Sample'),
      initialWidth: 100,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['true', 'false'],
        valueFormatter: (params: { value: string }) =>
          params.value === 'true'
            ? t('common.yes', 'Yes')
            : t('common.no', 'No'),
      } as ISetFilterParams,
    },
    {
      field: 'togId',
      headerName: t('animalManagement.columns.togId', 'TOG ID'),
      initialWidth: 80,
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'status',
      headerName: t('animalManagement.columns.status', 'Status'),
      initialWidth: 80,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['1', '2'],
        valueFormatter: (params: { value: string }) =>
          params.value === '1'
            ? t('common.active', 'Active')
            : t('common.inactive', 'Inactive'),
      } as ISetFilterParams,
    },
    {
      field: 'animalType',
      headerName: t('animalManagement.columns.animalType', 'Animal Type'),
      initialWidth: 105,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.values(ANIMAL_TYPE_CODES).map(String),
        valueFormatter: (params: { value: string }) =>
          ANIMAL_TYPE_CODE_LABELS[params.value] ?? params.value,
      } as ISetFilterParams,
    },
  ];
};

export const useProcedureColumns = (): ColDef<ProcedureRow>[] => {
  const { t } = useTranslation();

  return [
    selectionColumn,
    {
      field: 'procedureCode',
      headerName: t('animalManagement.columns.procedureCode', 'Procedure Code'),
      initialWidth: 130,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'procedureName',
      headerName: t('animalManagement.columns.procedureName', 'Procedure Name'),
      initialWidth: 195,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'description',
      headerName: t('animalManagement.columns.description', 'Description'),
      initialWidth: 300,
      flex: 1,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'procedureDate',
      headerName: t('animalManagement.columns.procedureDate', 'Procedure Date'),
      initialWidth: 120,
      filter: 'agDateColumnFilter',
    },
    {
      field: 'performedByName',
      headerName: t('animalManagement.columns.performedBy', 'Performed By'),
      initialWidth: 165,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'relatedAnimal',
      headerName: t('animalManagement.columns.relatedAnimal', 'Related Animal'),
      initialWidth: 120,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'relatedAnimalType',
      headerName: t(
        'animalManagement.columns.relatedAnimalType',
        'Animal Type',
      ),
      initialWidth: 120,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['Donor', 'Recipient', 'Calf'],
      } as ISetFilterParams,
    },
  ];
};

export function applyDefaultSetFilterValues(api: GridApi) {
  const columns: ColDef[] = api.getColumnDefs() ?? [];
  const setColumns = columns.filter(
    (col: ColDef) =>
      col.filter === 'agSetColumnFilter' &&
      col.field &&
      col.filterParams &&
      Array.isArray(col.filterParams.defaultValues),
  );
  if (!setColumns.length) {
    return;
  }

  let filtersChanged = false;

  for (const colDef of setColumns) {
    const field = colDef.field!;
    const currentFilterModel = api.getColumnFilterModel(field);
    if (
      currentFilterModel &&
      typeof currentFilterModel === 'object' &&
      Object.keys(currentFilterModel).length
    ) {
      continue;
    }

    api.setColumnFilterModel(field, {
      filterType: 'set',
      values: colDef.filterParams.defaultValues,
    });
    filtersChanged = true;
  }

  if (filtersChanged) {
    api.onFilterChanged('columnFilter');
  }
}
